import React, { useEffect, useState, useRef } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { XIcon, AlertCircleIcon, AlertTriangleIcon } from 'lucide-react';
import { assessRefundFunding, DEFAULT_ACQUIRER_REFUND_POLICY, type AcquirerRefundPolicy, type RefundFundingAssessment } from '../utils/refundFunding';
interface RefundModalProps {
  isOpen: boolean;
  onClose: () => void;
  transactionAmount: number;
  transactionId: string;
  currency?: string;
  /**
   * Merchant's available balance. Refunds are funded from this, not from the
   * original payment, so it governs what can actually be submitted.
   */
  availableBalance: number;
  /** Acquirer-level rule on whether the balance may go negative. */
  acquirerPolicy?: AcquirerRefundPolicy;
  onRefund: (amount: number, reason: string, notes: string) => void;
}
export function RefundModal({
  isOpen,
  onClose,
  transactionAmount,
  transactionId,
  currency = getTenant().currency.code,
  availableBalance,
  acquirerPolicy = DEFAULT_ACQUIRER_REFUND_POLICY,
  onRefund
}: RefundModalProps) {
  const {
    t
  } = useTranslation('refunds');
  const [refundAmount, setRefundAmount] = useState('');
  const [selectedReason, setSelectedReason] = useState('');
  const [notes, setNotes] = useState('');
  const [showReasonError, setShowReasonError] = useState(false);
  const refundReasons = ['Requested by customer', 'Duplicated', 'Fraudulent', 'Abandoned', 'Others'];
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const titleId = 'refund-modal-title';
  const descId = 'refund-modal-desc';
  const amountId = 'refund-modal-amount';
  const amountHelpId = 'refund-modal-amount-help';
  const reasonId = 'refund-modal-reason';
  const reasonHelpId = 'refund-modal-reason-help';
  const reasonErrorId = 'refund-modal-reason-error';
  const notesId = 'refund-modal-notes';
  const notesHelpId = 'refund-modal-notes-help';
  const handleClose = () => {
    setRefundAmount('');
    setSelectedReason('');
    setNotes('');
    setShowReasonError(false);
    onClose();
  };
  // Focus management: trap focus, restore on close, Esc handler
  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement as HTMLElement;
    // Focus first focusable element in dialog
    const focusFirst = () => {
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])');
      focusable?.[0]?.focus();
    };
    const t = setTimeout(focusFirst, 0);
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleClose();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])')).filter(el => !el.hasAttribute('disabled'));
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', handleKeyDown);
      previousFocusRef.current?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  const parsedAmount = parseFloat(refundAmount.replace(/,/g, '')) || 0;
  // One assessment drives BOTH the warning shown and whether submit works, so
  // the two can never disagree.
  const funding = assessRefundFunding({
    requestedAmount: parsedAmount,
    transactionAmount,
    availableBalance,
    policy: acquirerPolicy
  });
  const fmt = (n: number) => `${Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} ${currency}`;
  const signed = (n: number) => `${n < 0 ? '−' : ''}${fmt(n)}`;
  const setAmount = (n: number) => setRefundAmount(n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }));
  const handleRefundAll = () => setAmount(transactionAmount);
  const handleRefund = () => {
    if (!selectedReason) {
      setShowReasonError(true);
      return;
    }
    if (!funding.canSubmit) return;
    onRefund(parsedAmount, selectedReason, notes);
    handleClose();
  };
  const handleAmountChange = (value: string) => {
    const cleanValue = value.replace(/[^\d.]/g, '');
    const parts = cleanValue.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const formattedValue = decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
    setRefundAmount(formattedValue);
  };
  if (!isOpen) return null;
  // Reason is deliberately NOT part of this: it surfaces as an inline error
  // on click, which a disabled button would make unreachable.
  const isRefundDisabled = !funding.canSubmit;
  return <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity" onClick={handleClose} aria-hidden="true" data-id="element-9522" data-mp="mkycoe2qcooh" />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-9523" data-mp="djrrhrp4ypow">
        <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descId} className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-id="element-9524" data-mp="3380xcjjaidgt">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-9525" data-mp="34p7kfft40ln4">
            <div data-id="element-9526" data-mp="2qpoqbqx4vatt">
              <h2 id={titleId} className="text-xl font-semibold text-gray-900" data-id="element-9527" data-mp="2qq7yifwzekun">
                {t('components.RefundModal.refund_the_customer')}
              </h2>
              <p id={descId} className="text-sm text-gray-500 mt-1" data-id="element-9528" data-mp="2ut4l0hpn713v">
                {t('components.RefundModal.refunds_take_25_days_to_appear')}
              </p>
            </div>
            <button type="button" onClick={handleClose} aria-label={t('components.RefundModal.cancel') as string} className="text-gray-400 hover:text-gray-600 transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500" data-id="element-9529" data-mp="2lrwpg7eigedh">
              <XIcon size={24} aria-hidden="true" data-id="element-9530" data-mp="230dmu1oef2bz" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6" data-id="element-9531" data-mp="1jf3lxx35rzfk">
            {/* Refund Amount */}
            <div data-id="element-9532" data-mp="1eg0sl9wjjhnf">
              <label htmlFor={amountId} className="block text-sm font-medium text-gray-900 mb-2" data-id="element-9533" data-mp="1egixsw16l67h">
                {t('components.RefundModal.refund_amount')}
              </label>
              <div className="relative" data-id="element-9534" data-mp="1oc3wak71joch">
                <input id={amountId} type="text" inputMode="decimal" value={refundAmount} onChange={e => handleAmountChange(e.target.value)} placeholder="0.00" aria-describedby={amountHelpId} aria-required="true" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg" data-id="element-9535" data-mp="22cmyhaots06q" />
                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium" aria-hidden="true" data-id="element-9536" data-mp="2g5mcf88ylmc6">
                  {currency}
                </div>
              </div>
              <p id={amountHelpId} className="text-sm text-gray-500 mt-2" data-id="element-9537" data-mp="3shy61onj3zgx">
                {t('components.RefundModal.you_can_refund_up_to_the')}{' '}
                <span className="font-medium text-gray-900" data-id="element-9538" data-mp="5acmnxalb2f1">
                  {fmt(transactionAmount)}
                </span>
                .{' '}
                <button type="button" onClick={handleRefundAll} className="text-blue-600 hover:text-blue-700 font-medium underline-offset-2 hover:underline" data-id="element-9539" data-mp="34oprm12de5qa">
                  {t('components.RefundModal.refund_all')}
                </button>
              </p>
              {/* The balance is the funding source for this action, so it's
               always visible — a merchant shouldn't discover it only when a
               refund is refused. The negative allowance is shown alongside it
               only when there is one, since it's part of the same capacity. */}
              <p className="text-sm text-gray-500 mt-1" data-id="element-9540" data-mp="2dk2a3o7f9jum">
                Available balance{' '}
                <span className={`font-medium ${availableBalance <= 0 ? 'text-red-600' : 'text-gray-900'}`} data-id="element-9541" data-mp="28j64953ifnmk">
                  {signed(availableBalance)}
                </span>
                {acquirerPolicy.negativeBalanceLimit > 0 && <>
                    {' · '}plus{' '}
                    <span className="font-medium text-gray-900" data-id="element-9542" data-mp="1ur861pbc7c56">
                      {fmt(acquirerPolicy.negativeBalanceLimit)}
                    </span>{' '}
                    negative balance allowance
                  </>}
              </p>
              <FundingNotice funding={funding} fmt={fmt} signed={signed} onUseMax={() => setAmount(funding.maxRefundable)} data-id="element-9543" data-mp="1gt1hx0w6wqip" />
            </div>

            {/* Reason */}
            <div data-id="element-9544" data-mp="5pxvwcfu1liv">
              <label htmlFor={reasonId} className="block text-sm font-medium text-gray-900 mb-2" data-id="element-9545" data-mp="sxpg0f2mgr8">
                {t('components.RefundModal.reason')}
              </label>
              <select id={reasonId} value={selectedReason} onChange={e => {
              setSelectedReason(e.target.value);
              setShowReasonError(false);
            }} aria-required="true" aria-invalid={showReasonError || undefined} aria-describedby={`${reasonHelpId}${showReasonError ? ' ' + reasonErrorId : ''}`} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white ${showReasonError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'}`} style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%236B7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 1rem center'
            }} data-id="element-9546" data-mp="ai7hxhdtu1fv">
                <option value="" data-id="element-9547" data-mp="1tiq79n696a1h">
                  {t('components.RefundModal.select_a_reason')}
                </option>
                {refundReasons.map(reason => <option key={reason} value={reason} data-id="element-9548" data-mp="1kpmy3r2n4jcn">
                    {reason}
                  </option>)}
              </select>
              <p id={reasonHelpId} className="text-sm text-gray-500 mt-2" data-id="element-9549" data-mp="1oovn6imqjt1v">
                {t('components.RefundModal.this_is_only_visible_to_you')}
              </p>
              {showReasonError && <div id={reasonErrorId} role="alert" className="flex items-center gap-2 mt-2 text-sm text-red-600" data-id="element-9550" data-mp="2h942fmk4wp0a">
                  <AlertCircleIcon size={16} aria-hidden="true" data-id="element-9551" data-mp="2n264ltuj5qvd" />
                  <span data-id="element-9552" data-mp="2i18p5p0wnaxk">{t('components.RefundModal.required')}</span>
                </div>}
            </div>

            {/* Notes */}
            <div data-id="element-9553" data-mp="2d5kzz9mfahs2">
              <label htmlFor={notesId} className="block text-sm font-medium text-gray-900 mb-2" data-id="element-9554" data-mp="283vndpjjxw1v">
                {t('components.RefundModal.notes')}{' '}
                <span className="text-gray-400 font-normal" data-id="element-9555" data-mp="1zbk8n0xsvx1q">
                  {t('components.RefundModal.optional')}
                </span>
              </label>
              <textarea id={notesId} value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('components.RefundModal.attrs.add_more_details_about_this_refund') as string} rows={4} aria-describedby={notesHelpId} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none" data-id="element-9556" data-mp="1uek2kq0yauam" />
              <p id={notesHelpId} className="text-sm text-gray-500 mt-2" data-id="element-9557" data-mp="1p6u2sjza08s8">
                {t('components.RefundModal.this_is_only_visible_to_you')}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200" data-id="element-9558" data-mp="wjpxedw9tsfx">
            <button type="button" onClick={handleClose} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500" data-id="element-9559" data-mp="11gq1wkdh2n4x">
              {t('components.RefundModal.cancel')}
            </button>
            <button type="button" onClick={handleRefund} disabled={isRefundDisabled} className={`px-6 py-2 text-sm font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-900 ${isRefundDisabled ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-900 text-white hover:bg-gray-800'}`} data-id="element-9560" data-mp="2finsywa7im4q">
              {t('components.RefundModal.refund')}
            </button>
          </div>
        </div>
      </div>
    </>;
}

/**
 * Explains, in merchant terms, why a refund can or can't go through against
 * the current available balance. Silent when the balance covers the refund —
 * a warning that appears when nothing is wrong stops being read.
 */
function FundingNotice({
  funding,
  fmt,
  signed,
  onUseMax
}: {
  funding: RefundFundingAssessment;
  fmt: (n: number) => string;
  signed: (n: number) => string;
  onUseMax: () => void;
}) {
  const {
    outcome
  } = funding;
  if (outcome === 'idle' || outcome === 'funded') return null;
  if (outcome === 'exceeds_transaction') {
    return <Notice tone="error" data-id="element-9561" data-mp="3phqbf4b11aqv">
        <p data-id="element-9562" data-mp="3ariwgyk8a9ux">
          This is more than the original payment. Enter{' '}
          <span className="font-medium" data-id="element-9563" data-mp="3fq2l56q7hzqz">{fmt(funding.maxRefundable)}</span> or
          less.
        </p>
      </Notice>;
  }

  // Nothing left to draw on. Which constraint ran out decides the wording:
  // "you have no funds" and "you've used up your allowance" are different
  // problems with different resolutions.
  if (outcome === 'no_capacity') {
    return funding.capReason === 'negative_limit' ? <Notice tone="error" title="Negative balance limit reached" data-id="element-9564" data-mp="2wqydo2ebce1f">
        <p data-id="element-9565" data-mp="2wtax6jzpywgl">
          Your balance is already at the furthest negative your account
          permits, so there's nothing left to fund a refund. This will be
          possible once new payment activity settles and restores your
          balance.
        </p>
      </Notice> : <Notice tone="error" title="No available balance" data-id="element-9566" data-mp="tjzg86oubgwx">
        <p data-id="element-9567" data-mp="ojbqrpg9f84t">
          Refunds are funded from your available balance, and there's nothing
          available to draw on. This refund can't be submitted until new
          payment activity settles.
        </p>
      </Notice>;
  }

  // Beyond total capacity, but a smaller refund still fits.
  if (outcome === 'over_capacity') {
    return <Notice tone="warning" title={funding.capReason === 'negative_limit' ? 'Beyond your negative balance limit' : 'More than your available balance'} data-id="element-9568" data-mp="1c8e8syet32zu">
        <p data-id="element-9569" data-mp="yhr6kw9u6uz0">
          <span className="font-medium" data-id="element-9570" data-mp="yi0wkjuvt24p">{fmt(funding.unfundable)}</span> of this
          refund can't be funded
          {funding.capReason === 'negative_limit' ? ' — it would take your balance past the furthest negative your account permits' : ''}
          . You can refund the covered amount now and the rest once more funds
          settle.
        </p>
        <button type="button" onClick={onUseMax} className="mt-2 text-sm font-medium text-amber-900 underline underline-offset-2 hover:no-underline rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-amber-600" data-id="element-9571" data-mp="38y1gg48bqbt5">
          Refund {fmt(funding.maxRefundable)} instead
        </button>
      </Notice>;
  }

  // Goes negative, but inside the allowance the acquirer permits.
  return <Notice tone="warning" title="This will take your balance negative" data-id="element-9572" data-mp="mf5jomepgwx">
      <p data-id="element-9573" data-mp="5g1aeumbwgi6">
        <span className="font-medium" data-id="element-9574" data-mp="5fi0o16u0yf8">{fmt(funding.drawnFromAllowance)}</span>{' '}
        of this refund isn't covered by your available balance, which will go
        to{' '}
        <span className="font-medium" data-id="element-9575" data-mp="egfxnzius5ob">{signed(funding.projectedBalance)}</span>
        , leaving{' '}
        <span className="font-medium" data-id="element-9576" data-mp="jfssb6fyuh1g">{fmt(funding.remainingAllowance)}</span>{' '}
        of your negative balance allowance. The shortfall is recovered from
        your next payouts as a negative balance recovery.
      </p>
    </Notice>;
}
function Notice({
  tone,
  title,
  children
}: {
  tone: 'warning' | 'error';
  title?: string;
  children: React.ReactNode;
}) {
  const isError = tone === 'error';
  const Icon = isError ? AlertCircleIcon : AlertTriangleIcon;
  return <div role="alert" className={`mt-3 flex gap-2.5 rounded-lg border p-3 text-sm ${isError ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-900'}`} data-id="element-9577" data-mp="37pyypwnrn9r1">
      <Icon size={16} aria-hidden="true" className={`mt-0.5 shrink-0 ${isError ? 'text-red-600' : 'text-amber-600'}`} data-id="element-9578" data-mp="3gkd85ilm7q28" />
      <div data-id="element-9579" data-mp="2o29dk8ac47qs">
        {title && <p className="font-medium" data-id="element-9580" data-mp="2o1g24u0fku2b">{title}</p>}
        {children}
      </div>
    </div>;
}